import { FormEvent, useEffect, useMemo, useState } from 'react';
import OnelivePcMypageShell from './OnelivePcMypageShell';
import { EXCHANGE_BANKS } from './exchangeBanks';
import {
  fetchExchangeConfig,
  fetchItemShopWallet,
  formatAmount,
  getCurrentUser,
  resolveRealName,
  showOneliveAlert,
  submitExchangeRequest,
  type ExchangeConfig,
} from './api';

const DEFAULT_EXCHANGE_GUIDE = [
  '첫환전은 (5%수수료차감) 2번째환전은 (10%수수료차감) 환전시간 딜레이없이 당일 즉시 환전가능합니다.',
  '환전 신청완료시 업무가 많으면 출금시간이 5분~10분정도 지연될수 있습니다.',
];

function parseGuideLines(html: string): string[] {
  if (!html.trim()) return DEFAULT_EXCHANGE_GUIDE;
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ');
  const lines = withBreaks
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return lines.length > 0 ? lines : DEFAULT_EXCHANGE_GUIDE;
}

export default function OneliveExchangeRequestPage() {
  const [bankName, setBankName] = useState('');
  const [bankNumber, setBankNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [coinBalance, setCoinBalance] = useState(0);
  const [exchangeConfig, setExchangeConfig] = useState<ExchangeConfig | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const user = getCurrentUser();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [wallet, config] = await Promise.all([
          fetchItemShopWallet(),
          fetchExchangeConfig(),
        ]);
        if (mounted && wallet) setCoinBalance(wallet.coin_balance ?? 0);
        if (mounted && config) setExchangeConfig(config);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const realName = resolveRealName(user);
    if (realName) setAccountName(realName);
    if (user?.mobile) setPhone(user.mobile.replace(/\D/g, ''));
  }, [user]);

  const minAmount = exchangeConfig?.settings?.min_amount ?? 100000;
  const guideLines = useMemo(
    () => parseGuideLines(exchangeConfig?.settings?.content_head ?? ''),
    [exchangeConfig?.settings?.content_head],
  );

  const totalExchange = useMemo(() => {
    const num = Number(amount);
    return Number.isFinite(num) && num > 0 ? num : 0;
  }, [amount]);

  const onAmountChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      setAmount('');
      return;
    }
    let num = Number(digits);
    if (!Number.isFinite(num) || num < 0) {
      setAmount('');
      return;
    }
    if (num > coinBalance) num = coinBalance;
    setAmount(String(num));
  };

  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();

    if (!bankName) {
      showOneliveAlert('은행명 선택하세요.');
      return;
    }
    if (!bankNumber.trim()) {
      showOneliveAlert('계좌번호 입력하세요.');
      return;
    }
    if (!accountName.trim()) {
      showOneliveAlert('예금주 입력하세요.');
      return;
    }

    const num = Number(amount);
    if (!amount || !Number.isFinite(num) || num <= 0) {
      showOneliveAlert('환전금액 입력하세요.');
      return;
    }
    if (num > coinBalance) {
      showOneliveAlert('보유하신 코인이 부족합니다.');
      return;
    }
    if (num < minAmount) {
      showOneliveAlert('환전금액은 최소10만입니다.');
      return;
    }

    if (exchangeConfig && !exchangeConfig.is_active) {
      showOneliveAlert(exchangeConfig.settings?.error_message || '환전 신청이 일시 중단되었습니다.');
      return;
    }

    setSubmitting(true);
    try {
      const holder = accountName.trim();
      const result = await submitExchangeRequest({
        amount: num,
        bank_name: bankName,
        bank_number: bankNumber.trim(),
        account_name: holder,
        phone: phone.trim(),
        content: holder ? `${bankName} ${holder}` : bankName,
      });
      const message = result.message
        || exchangeConfig?.settings?.success_message
        || '환전 신청이 접수되었습니다.';
      setAmount('');
      const wallet = await fetchItemShopWallet();
      if (wallet) setCoinBalance(wallet.coin_balance ?? 0);
      showOneliveAlert(message);
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || '환전 신청 처리 중 오류가 발생했습니다.';
      showOneliveAlert(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnelivePcMypageShell pageClassName="onelive-exchange-request-page">
      <div className="onelive-exchange-request-wrap">
        <form
          className="onelive-exchange-form"
          onSubmit={submitRequest}
          autoComplete="off"
        >
          <div className="item-detail mar-cash mar-t-10">
            <div className="proceeding-chat">
              <div>
                <div className="pro-chat-tit onelive-pro-chat-tit">
                  <h3>코인 환전신청</h3>
                </div>
                <div className="exchange mar-cash">
                  <div className="ex-data">
                    <div>
                      <select
                        className="form-control"
                        name="mn_bank_name"
                        value={bankName}
                        onChange={(ev) => setBankName(ev.target.value)}
                      >
                        <option value="">은행을 선택해주세요</option>
                        {EXCHANGE_BANKS.map((bank) => (
                          <option key={bank} value={bank}>{bank}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <h3>＂계좌번호</h3>
                      <input
                        type="text"
                        name="mn_bank_number"
                        className="form-control"
                        value={bankNumber}
                        onChange={(ev) => setBankNumber(ev.target.value.replace(/\D/g, ''))}
                        placeholder="-부호 없이 숫자만 입력해주세요"
                        autoComplete="off"
                        onCopy={(ev) => ev.preventDefault()}
                        onCut={(ev) => ev.preventDefault()}
                        onPaste={(ev) => ev.preventDefault()}
                        onContextMenu={(ev) => ev.preventDefault()}
                      />
                    </div>

                    <div>
                      <h3>＂예금주명</h3>
                      <input
                        type="text"
                        name="mn_name"
                        className="form-control"
                        value={accountName}
                        onChange={(ev) => setAccountName(ev.target.value)}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <h3>＂휴대폰번호</h3>
                      <input
                        type="text"
                        name="mn_hp"
                        className="form-control"
                        value={phone}
                        onChange={(ev) => setPhone(ev.target.value.replace(/\D/g, ''))}
                        placeholder="-부호 없이 숫자만 입력해주세요"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <h3>＂코인개수</h3>
                      <input
                        type="number"
                        name="mn_subject"
                        className="form-control"
                        value={amount}
                        onChange={(ev) => onAmountChange(ev.target.value)}
                        placeholder="-부호 없이 숫자만 입력해주세요"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="proceeding-chat onelive-exchange-guide-box">
                    <ul className="proceeding-bbbb">
                      <li>
                        <b>★환전안내★</b>
                      </li>
                      {guideLines.map((line) => (
                        <li key={line}>
                          <p>{line}</p>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="ex-cash onelive-exchange-balance-row">
                    <span className="onelive-exchange-balance">
                      (보유코인:
                      <span className="t_point">{loading ? '...' : formatAmount(coinBalance)}</span>
                      )
                    </span>
                    <span className="text-red" aria-hidden="true" />
                  </div>

                  <div className="ex-cash">
                    <span>총 환전예정 금액</span>
                    <span className="text-red" id="totalExchange">
                      {formatAmount(totalExchange)}
                    </span>
                  </div>

                  <div className="ex-data mar-t-15">
                    <button type="submit" className="btn btn-info" disabled={submitting}>
                      {submitting ? '처리 중...' : '환전신청'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        {submitting ? (
          <div className="onelive-exchange-loading" role="status" aria-live="polite">
            <p>환전 신청 처리 중...</p>
          </div>
        ) : null}
      </div>
    </OnelivePcMypageShell>
  );
}
